import type { AbilityDemo } from '@/features/playbook/types';

const demo: AbilityDemo = {
  id: 'artes-liberales',
  slug: 'artes-liberales',
  label: 'Artes Liberales',
  roll: {
    formula: 'Int 2 + Artes Liberales 2 = 4 + die',
    niche: '+1 when decoding grammar or rhetoric',
  },
  whenToUse: [
    'Parse the grammatical structure of ancient texts or formal documents',
    'Apply logical reasoning to solve riddles, puzzles, or scholarly debates',
    'Compose or critique formal correspondence and rhetorical arguments',
    'Compute astronomical cycles or geometric proportions in diagrams',
    'Reading and writing — the basic literacy underlying scribe work',
  ],
  samples: [
    `GM: The Ex Astra covenant scouts a ruined chapel in spring 1195. Its eastern wall bears a Latin inscription, weathered and half-effaced—the syntax deliberate and obscure.
You (Mikaela): I trace each visible letter, piecing together the grammatical structure beneath the damage. Grammar teaches me that even partial words reveal their role in the sentence.
GM: Roll Int 2 + Artes Liberales 2 = 4 + die

Teaches: Artes Liberales 2 / total 4 + die; niche Grammar (+1) = 5. Even partial words reveal their grammatical role beneath damage and time.`,
    `GM: The party has recovered a rough map showing landmarks and distances from Ex Astra in spring 1195. Elara needs you to compute the covenant's distance from the ruins where you found ingredients.
You (Mikaela): I apply geometry to estimate the map's scale, then arithmetic to sum the distances. Perfectionism drives me to check the cardinal directions twice.
GM: Roll Int 2 + Artes Liberales 2 = 4 + die

Teaches: Artes Liberales 2 / total 4 + die; Perfectionist +2 slows her but ensures accuracy across multiple coordinate checks.`,
    `GM: "[Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
GM: Brother Alfric points to unfamiliar constellations at dusk and asks how long until dawn. The positions matter, but you are uncertain.
You (Mikaela): I recall the astronomy underlying the liberal arts—how celestial bodies move in predictable patterns. I map the visible constellations to the season and estimate the hour.
GM: Roll Int 2 + Artes Liberales 2 = 4 + die

Teaches: Artes Liberales 2 / total 4 + die; astronomy is one of the seven liberal arts, taught through pure logic and observation.`,
    `GM: "[Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
GM: Sir Helgi holds a scroll retrieved from scouting: Ex Astra's mutual defense treaty with a neighboring House. The clauses are tangled syntax, and he must know what the covenant genuinely pledged.
You (Mikaela): I parse the logical structure sentence by sentence—each condition, each consequence—isolating the true obligation beneath the legal rhetoric.
GM: Roll Int 2 + Artes Liberales 2 = 4 + die

Teaches: Artes Liberales 2 / total 4 + die; Clerk virtue grants this Academic ability. Mikaela's scholar's hands parse the logical framework beneath tangled syntax.`,
    `GM: "[Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
GM: A letter arrives from a rival magus congratulating your covenant on spring's success. Corvus detects something barbed beneath the praise—flattery masking an insult.
You (Mikaela): I examine the rhetorical flow, word choice, and how praise is qualified. The structure of the argument reveals the speaker's true intent beneath surface courtesy.
GM: Roll Int 2 + Artes Liberales 2 = 4 + die

Teaches: Artes Liberales 2 / total 4 + die; niche Grammar (+1) = 5. Rhetoric is the formal study of persuasion; Mikaela reads the skeleton of the argument.`,
  ],
  vignette: 'Mikaela squints at the weathered manuscript, tracing each Latin phrase with her finger. She knows the words well enough—her Latin is solid—but Artes Liberales teaches her to see the skeleton beneath: the grammar that locks meaning into place, the logical flow from premise to conclusion. She will not trust her instinct alone.',
  gotcha: 'Artes Liberales is the theoretical framework underlying formal reasoning; Latin is the language itself. You might speak Latin fluently but stumble on the formal logic of a syllogism, or vice versa.',
};

export default demo;
